/**
 * Delivery Postcode Gate
 * File: assets/delivery-postcode-gate.js
 *
 * Pickup   -> field hidden, checkout enabled.
 * Delivery -> field shown, checkout disabled until a valid in-area postcode is entered.
 *
 * Matching: normalise -> validate UK format -> outward = all but last 3 chars ->
 *           exact match against data-allowed. No prefix matching.
 *
 * UX guard only. Authoritative gates are:
 *   1. snippets/delivery-checkout-gate.liquid (server-rendered disabled state)
 *   2. Shopify shipping zones at checkout
 */

class DeliveryPostcodeGate extends HTMLElement {
  static POSTCODE_RE = /^[A-Z]{1,2}\d[A-Z\d]?\d[A-Z]{2}$/;

  constructor() {
    super();
    this.isValid = false;
    this.lastWritten = null;
    this._writeTimer = null;
    this.onInput = this.onInput.bind(this);
    this.onPreferenceChanged = this.onPreferenceChanged.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
  }

  connectedCallback() {
    this.field = this.querySelector('[data-postcode-field]');
    this.input = this.querySelector('[data-postcode-input]');
    this.message = this.querySelector('[data-postcode-message]');

    this.allowed = new Set(
      (this.getAttribute('data-allowed') || '')
        .split(',')
        .map((c) => c.replace(/[^A-Z0-9]/gi, '').toUpperCase())
        .filter(Boolean)
    );

    this.preference = (this.getAttribute('data-preference') || 'pickup').toLowerCase();

    // Scope: drawer footer, main cart form, or document as last resort.
    const scope =
      this.closest('[id^="CartDrawerFooter"]') ||
      this.closest('form[action*="/cart"]')?.parentElement ||
      this.closest('.cart__footer') ||
      document;

    this.forms = Array.from(scope.querySelectorAll('form')).filter((f) =>
      f.querySelector('[name="checkout"]')
    );
    this.checkoutBtns = Array.from(scope.querySelectorAll('[name="checkout"]'));

    // Seed lastWritten so we don't re-write an unchanged value on first render.
    this.lastWritten = this.input ? this.normalise(this.input.value) : '';

    if (this.input) this.input.addEventListener('input', this.onInput);
    this.forms.forEach((f) => f.addEventListener('submit', this.onSubmit));
    document.addEventListener('delivery:preference-changed', this.onPreferenceChanged);

    this.applyPreference(this.preference, { focus: false });
  }

  disconnectedCallback() {
    if (this.input) this.input.removeEventListener('input', this.onInput);
    if (this.forms) this.forms.forEach((f) => f.removeEventListener('submit', this.onSubmit));
    document.removeEventListener('delivery:preference-changed', this.onPreferenceChanged);
    clearTimeout(this._writeTimer);
  }

  /* ---------- Core ---------- */

  normalise(value) {
    return (value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  }

  check(value) {
    const pc = this.normalise(value);
    if (!pc) return { ok: false, reason: 'empty' };
    if (!DeliveryPostcodeGate.POSTCODE_RE.test(pc)) return { ok: false, reason: 'format' };
    const outward = pc.slice(0, -3);
    if (!this.allowed.has(outward)) return { ok: false, reason: 'area' };
    return { ok: true, reason: null };
  }

  onInput() {
    if (this.preference !== 'delivery' || !this.input) return;

    const value = this.input.value;
    const result = this.check(value);
    this.isValid = result.ok;

    if (result.ok) {
      this.input.setAttribute('aria-invalid', 'false');
      this.showMessage('success', 'Great — we deliver to your area.');
      this.setCheckoutEnabled(true);
      this.queueWritePostcode(this.normalise(value));
      return;
    }

    this.input.setAttribute('aria-invalid', value ? 'true' : 'false');
    this.setCheckoutEnabled(false);
    this.queueWritePostcode('');

    if (result.reason === 'empty') {
      this.hideMessage();
    } else if (result.reason === 'format') {
      this.showMessage('error', 'Please enter a full, valid UK postcode.');
    } else {
      this.showMessage('error', "Sorry, we don't deliver to your area. Please select Pickup.");
    }
  }

  onPreferenceChanged(event) {
    const pref = (event.detail && event.detail.preference) || 'pickup';
    this.applyPreference(pref, { focus: pref === 'delivery' });
  }

  applyPreference(pref, opts) {
    this.preference = (pref || 'pickup').toLowerCase();

    if (this.preference !== 'delivery') {
      if (this.field) this.field.classList.remove('is-visible');
      this.hideMessage();
      this.isValid = true;
      this.setCheckoutEnabled(true);
      return;
    }

    if (this.field) this.field.classList.add('is-visible');

    // Re-validate the repopulated value against the CURRENT allowed list.
    const result = this.check(this.input ? this.input.value : '');
    this.isValid = result.ok;
    this.setCheckoutEnabled(result.ok);

    if (result.ok) {
      this.showMessage('success', 'Great — we deliver to your area.');
    } else if (result.reason === 'area') {
      // Stale saved postcode now out of area: clear it so the server gate agrees.
      this.showMessage('error', "Sorry, we don't deliver to your area. Please select Pickup.");
      this.queueWritePostcode('');
    } else {
      this.hideMessage();
    }

    if (opts && opts.focus && this.input) {
      try {
        this.input.focus();
      } catch (e) {
        /* noop */
      }
    }
  }

  /* ---------- Helpers ---------- */

  setCheckoutEnabled(enabled) {
    if (!this.checkoutBtns || !this.checkoutBtns.length) return;
    this.checkoutBtns.forEach((btn) => {
      btn.disabled = !enabled;
      btn.setAttribute('aria-disabled', String(!enabled));
    });
  }

  showMessage(type, text) {
    if (!this.message) return;
    this.message.textContent = text;
    this.message.className = 'postcode-gate__message is-shown postcode-gate__message--' + type;
  }

  hideMessage() {
    if (!this.message) return;
    this.message.textContent = '';
    this.message.className = 'postcode-gate__message';
  }

  queueWritePostcode(value) {
    if (value === this.lastWritten) return; // no redundant cart writes
    clearTimeout(this._writeTimer);
    this._writeTimer = setTimeout(() => this.writePostcode(value), 600);
  }

  async writePostcode(value) {
    if (value === this.lastWritten) return;
    this.lastWritten = value;
    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { delivery_postcode: value } }),
      });
      // Deliberately NOT firing cart:refresh — it would wipe the field mid-typing.
    } catch (e) {
      this.lastWritten = null; // allow a retry on the next keystroke
    }
  }

  onSubmit(event) {
    if (this.preference === 'delivery' && !this.isValid) {
      event.preventDefault();
      this.showMessage('error', "Sorry, we don't deliver to your area. Please select Pickup.");
      if (this.input) {
        try {
          this.input.focus();
        } catch (e) {
          /* noop */
        }
      }
    }
  }
}

if (!customElements.get('delivery-postcode-gate')) {
  customElements.define('delivery-postcode-gate', DeliveryPostcodeGate);
}